import { z } from 'zod';
import { Activity, JsonValue, SaveData, SpeechEvaluationRequestData, SubmitData } from '../bridge/types';

const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ]),
);

const choiceSchema = z
  .object({
    label: z.string(),
    isCorrect: z.boolean(),
  })
  .refine(
    (data) => {
      try {
        JSON.stringify(data);
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Value must be serializable' },
  );

const baseActivityObject = {
  activityId: z.string(),
  type: z.enum(['MULTIPLE_CHOICE', 'SHORT_ANSWER', 'INTERACTIVE', 'SPEAKING'] as const),
};

// 객관식 type 스키마
const multipleChoiceSchema = z
  .object({
    ...baseActivityObject,
    type: z.literal('MULTIPLE_CHOICE'),
    choices: z.array(choiceSchema).min(1),
  })
  .refine(
    (data) => {
      try {
        JSON.stringify(data);
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Value must be serializable' },
  );

// 주관식 type 스키마
const shortAnswerSchema = z
  .object({
    ...baseActivityObject,
    type: z.literal('SHORT_ANSWER'),
    answer: z.string().min(1).trim(),
  })
  .refine(
    (data) => {
      try {
        JSON.stringify(data);
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Value must be serializable' },
  );

// 활동형 type 스키마
const interactiveSchema = z
  .object({
    ...baseActivityObject,
    type: z.literal('INTERACTIVE'),
  })
  .refine(
    (data) => {
      try {
        JSON.stringify(data);
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Value must be serializable' },
  );

// 말하기 type 스키마
const speakingSchema = z
  .object({
    ...baseActivityObject,
    type: z.literal('SPEAKING'),
    speakingType: z.enum(['STT_ONLY', 'PHONICS', 'PRONUNCIATION'] as const),
  })
  .refine(
    (data) => {
      try {
        JSON.stringify(data);
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Value must be serializable' },
  );

// 제출 데이터 스키마
const submitItemSchema = z.union([
  z
    .object({
      activityId: z.string(),
      userSelectedIndexes: z.array(z.number()),
    })
    .strict(),
  z
    .object({
      activityId: z.string(),
      userAnswer: z.string(),
    })
    .strict(),
  z
    .object({
      activityId: z.string(),
    })
    .strict(), // 활동 타입과 말하기 타입은 추가 데이터 없이 제출 가능
]);

// Validation functions
export const isValidChoice = (choice: unknown): choice is z.infer<typeof choiceSchema> => {
  return choiceSchema.safeParse(choice).success;
};

export const isValidMultipleChoiceData = (
  activity: unknown,
): activity is z.infer<typeof multipleChoiceSchema> => {
  return multipleChoiceSchema.safeParse(activity).success;
};

export const isValidShortAnswerData = (activity: unknown): activity is z.infer<typeof shortAnswerSchema> => {
  return shortAnswerSchema.safeParse(activity).success;
};

export const isValidInteractiveData = (activity: unknown): activity is z.infer<typeof interactiveSchema> => {
  return interactiveSchema.safeParse(activity).success;
};

export const isValidSpeakingData = (activity: unknown): activity is z.infer<typeof speakingSchema> => {
  return speakingSchema.safeParse(activity).success;
};

export const isValidSubmitData = (data: unknown): data is SubmitData[] => {
  const submitArraySchema = z.array(submitItemSchema);
  return submitArraySchema.safeParse(data).success;
};

export const isValidActivityId = (data: Activity[]): boolean => {
  const activityIds = data.map((item) => item.activityId);
  const uniqueIds = new Set(activityIds);
  return activityIds.length === uniqueIds.size;
};

// 직렬화 가능 여부 검증
export const isSerializable = (value: unknown): value is JsonValue => {
  return jsonValueSchema.safeParse(value).success;
};

// SaveData 스키마 추가
const saveDataSchema = z.record(jsonValueSchema);

export const isValidSaveData = (data: unknown): data is SaveData => {
  return saveDataSchema.safeParse(data).success;
};

// 발화평가 요청 데이터 스키마
const speechEvaluationRequestSchema = z
  .object({
    activityId: z.string(),
    speakingType: z.enum(['STT_ONLY', 'PHONICS', 'PRONUNCIATION'] as const),
    url: z.string().url(),
    referenceText: z.string(),
  })
  .strict();

export const isValidRequestSpeechEvaluationData = (data: unknown): data is SpeechEvaluationRequestData => {
  return speechEvaluationRequestSchema.safeParse(data).success;
};

// 스키마 export 확장
export const schemas = {
  choice: choiceSchema,
  multipleChoice: multipleChoiceSchema,
  shortAnswer: shortAnswerSchema,
  interactive: interactiveSchema,
  speaking: speakingSchema,
  submitItem: submitItemSchema,
  jsonValue: jsonValueSchema,
  saveData: saveDataSchema,
  speechEvaluationRequest: speechEvaluationRequestSchema,
} as const;
